import React, { useCallback, useMemo, useRef } from 'react'

import { pathOr } from 'ramda'
import { useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'

import { Div } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Subnav } from '@driftt/tide-subnav'

import { animateChartWidth } from 'components/charts/highcharts/utils'
import useFilters from 'hooks/useFilters'
import useHasCoverageReportAccess from 'hooks/useHasCoverageReportAccess'
import RealTimeDot from 'layouts/RealTimeDashboard/components/RealTimeDot'
import { getFilterQueryString } from 'modules/filter/utils'
import { orgCreatedAt as orgCreatedAtSelector } from 'modules/org/selectors'
import {
  hasDemoOrgGate,
  hasDriftVoice,
  hasRealTimeDashboard,
  hasRevenueReportAccess,
  isEmailOnlyOrg,
  onlySearch,
  showConvoAnalysisInReports,
  userHasChatPlaybookAccess,
  userHasConversationReportsAccess,
  userHasCSATReports,
  userHasEmailCampaignReportAccess,
  userHasEmailPlaybooksAccess,
  userHasFastlanePlaybookAccess,
  userHasRoutingAccess,
  userHasSalesResultsAccess,
  userHasSalesUsageReportsFeature,
  userHasServiceReports,
  userHasSiteConciergeAccess,
  userHasTeamPerformanceAccess,
  userHasTeamUsageDashboardFeature,
} from 'utils/access'

import styles from './styles.module.css'

const isBotSelector = pathOr(false, ['user', 'bot'])

const AppSubnav = () => {
  const isBot = useSelector(isBotSelector)
  const { timePeriod } = useFilters()
  const hasCoverageReportAccess = useHasCoverageReportAccess()
  const subnavOpenRef = useRef(true)
  const animatingChartRef = useRef<{ cancel: () => void } | undefined>()
  const orgCreatedAt = useSelector(orgCreatedAtSelector)

  // Hide Convo analysis for all orgs created after Nov 1, 2023
  const hideConvoAnalysis = orgCreatedAt >= 1698796800000

  const queryString = getFilterQueryString(timePeriod)

  const onSubnavToggle = useCallback(() => {
    if (animatingChartRef.current) {
      animatingChartRef.current.cancel()
    }
    animatingChartRef.current = animateChartWidth(500)
    subnavOpenRef.current = !subnavOpenRef.current
  }, [])

  const nodes = useMemo(() => {
    const toNode = (
      label: string | JSX.Element,
      path: string,
      nodeProps?: Record<string, unknown>
    ) => {
      const { excludeSearch = false, ...rest }: any = nodeProps || {}

      return {
        label,
        path: { pathname: path, search: !excludeSearch ? queryString : '' },
        exact: false,
        external: false,
        ...rest,
      }
    }

    const emailGroup = {
      label: 'EMAIL',
      expanded: true,
      exact: false,
      external: false,
      nodes: [toNode('Campaigns', '/email/campaigns')],
    }

    if (isEmailOnlyOrg()) {
      return [emailGroup]
    }

    const result: any[] = []

    if (hasRealTimeDashboard()) {
      result.push(
        toNode(
          <>
            <RealTimeDot />
            <span>Real-time dashboard</span>
          </>,
          '/real-time',
          { excludeSearch: true }
        )
      )
    }

    if (userHasSiteConciergeAccess()) {
      result.push(
        toNode('Site concierge', '/site-concierge', {
          className: styles.siteConciergeNavItem,
        })
      )
    }
    if (onlySearch()) {
      result.push(
        toNode('Intelligent search', '/intelligent-search', {
          className: styles.intelligentSearchNavItem,
        })
      )
    }

    if (hasRevenueReportAccess()) {
      const revenue = {
        label: 'REVENUE',
        expanded: true,
        exact: false,
        external: false,
        nodes: [
          { label: 'Opportunity overview', path: '/revenue/overview' },
          { label: 'Opportunity data', path: '/revenue/opportunities' },
        ],
      }

      result.push(revenue)
    }

    if (
      userHasChatPlaybookAccess() ||
      hasCoverageReportAccess ||
      userHasFastlanePlaybookAccess() ||
      userHasEmailPlaybooksAccess()
    ) {
      const playbooksGroup = toNode('PLAYBOOKS', '/', {
        expanded: true,
        nodes: [
          toNode('Chat playbooks', '/playbooks/chat', {
            hide: !userHasChatPlaybookAccess(),
            isActive: (_, location) =>
              location.pathname.includes('/playbooks/chat') &&
              !location.pathname.includes('site-coverage'),
          }),
          toNode('Site coverage', '/playbooks/chat/site-coverage', {
            excludeSearch: true,
            hide: !hasCoverageReportAccess,
          }),
          toNode('Fastlane playbooks', '/playbooks/fastlane', {
            hide: !userHasFastlanePlaybookAccess(),
          }),
          toNode('Email playbooks', '/playbooks/email', {
            hide: !userHasEmailPlaybooksAccess(),
          }),
        ],
      })

      result.push(playbooksGroup)
    }

    if (userHasConversationReportsAccess() || showConvoAnalysisInReports()) {
      const conversationsGroup = {
        label: 'CONVERSATIONS',
        expanded: true,
        exact: false,
        external: false,
        nodes: [
          {
            label: 'Chat overview',
            path: '/conversations',
            hide: !userHasConversationReportsAccess(),
          },
          {
            label: 'Routed chats',
            path: '/routing',
            hide: !userHasRoutingAccess(),
          },
          {
            label: 'Chat data',
            path: '/conversations/data',
            hide: !userHasConversationReportsAccess(),
          },
          {
            label:
              !showConvoAnalysisInReports() && isBot ? (
                <Div>
                  <Icon
                    name="lock"
                    className={styles.navBotLockIcon}
                    width={12}
                    height={12}
                  />
                  Conversation analysis
                </Div>
              ) : (
                'Conversation analysis'
              ),
            path: '/conversations/analysis',
            hide:
              (!showConvoAnalysisInReports() && !isBot) || hideConvoAnalysis,
          },
          {
            label: 'Meetings',
            path: '/meetings',
            exact: false,
            hide: !userHasConversationReportsAccess(),
          },
          {
            label: 'Calls',
            path: '/calls',
            hide: !hasDriftVoice(),
            exact: false,
          },
        ],
      }

      result.push(conversationsGroup)
    }

    if (userHasTeamPerformanceAccess()) {
      result.push({
        label: 'TEAM PERFORMANCE',
        expanded: true,
        exact: false,
        external: false,
        nodes: [
          toNode('Availability', '/availability', {
            excludeSearch: true,
          }),
          toNode('Sales Rep setup ', '/team/sales-setup', {
            hide: !userHasTeamUsageDashboardFeature(),
          }),
          toNode('Sales Rep usage', '/team/sales-usage', {
            hide: !userHasSalesUsageReportsFeature(),
          }),
        ],
      })
    }

    if (hasDemoOrgGate() || userHasSalesResultsAccess()) {
      const salesItems = {
        label: 'SALES RESULTS',
        expanded: true,
        exact: false,
        external: false,
        nodes: [
          { label: 'Overview', path: '/sales/overview' },
          { label: 'Pipeline', path: '/sales/pipeline' },
          { label: 'Closed won', path: '/sales/closed-won' },
          { label: 'Sales cycle', path: '/sales/sales-cycle' },
        ],
      }

      result.push(salesItems)
    }

    if (userHasServiceReports()) {
      const salesItems = {
        label: 'SERVICE RESULTS',
        expanded: true,
        exact: false,
        external: false,
        nodes: [
          { label: 'Deflection', path: '/service/deflection' },
          userHasCSATReports() && { label: 'CSAT', path: '/service/csat' },
        ].filter(Boolean),
      }

      result.push(salesItems)
    }

    if (userHasEmailCampaignReportAccess()) {
      result.push(emailGroup)
    }

    return result
  }, [queryString, hasCoverageReportAccess, isBot, hideConvoAnalysis])

  return (
    <Subnav
      title="Reports"
      open
      NavLink={NavLink}
      nodes={nodes}
      onToggle={onSubnavToggle}
    />
  )
}

export default AppSubnav
